// src/components/ProductEditorModal.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Textarea,
  Switch,
  Button,
  NumberInput,
  NumberInputField,
  Divider,
  IconButton,
  Badge,
  Tooltip,
  Select,
  useColorModeValue,
  Text,
  Checkbox,
  SimpleGrid,
  Tag,
  TagLabel,
  TagCloseButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, InfoIcon } from "@chakra-ui/icons";

/* ---------- utils ---------- */

const validateJson = (text) => {
  try {
    const obj = JSON.parse(text);
    return { ok: true, value: obj };
  } catch (e) {
    return { ok: false, error: e?.message || "Invalid JSON" };
  }
};

// Unwrap DynamoDB AttributeValue-like objects (e.g., {S:"..."})
const unwrap = (v) => {
  if (v && typeof v === "object" && "S" in v) return v.S;
  return v;
};

const normalizePartNumbers = (pn) => {
  const out = {};
  Object.entries(pn || {}).forEach(([k, v]) => {
    const sv = unwrap(v);
    if (sv != null) out[k] = String(sv);
  });
  return out;
};

const unique = (arr) => Array.from(new Set(arr || []));

const mapToPairs = (obj) =>
  Object.entries(obj || {}).map(([k, v]) => ({ key: String(k), value: String(v ?? "") }));

const pairsToMap = (pairs, { numericValues = false } = {}) =>
  pairs.reduce((acc, { key, value }) => {
    const k = (key || "").trim();
    if (!k) return acc;
    acc[k] = numericValues ? Number(value) : value;
    return acc;
  }, {});

const arrToPairs = (arr) => (arr || []).map((s) => ({ key: String(s || ""), value: "" }));
const pairsToArr = (pairs) =>
  pairs
    .map((p) => (p.key || "").trim())
    .filter(Boolean);

const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

/* ---------- PairListEditor ---------- */

const PairListEditor = ({
  leftLabel,
  rightLabel,
  pairs,
  setPairs,
  leftPlaceholder,
  rightPlaceholder,
  rightType = "text",
  helper,
}) => {
  const border = useColorModeValue("gray.100", "gray.700");

  const setRow = (i, field, val) =>
    setPairs((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: val };
      return next;
    });

  const addRow = () => setPairs((prev) => [...prev, { key: "", value: "" }]);
  const removeRow = (i) => setPairs((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <VStack align="stretch" spacing={3}>
      <VStack align="stretch" spacing={2}>
        {pairs.map((row, idx) => (
          <HStack
            key={idx}
            spacing={3}
            align="center"
            borderWidth="1px"
            borderColor={border}
            p={2}
            borderRadius="md"
          >
            <FormControl>
              <FormLabel mb={1} fontSize="xs" color="gray.500">
                {leftLabel}
              </FormLabel>
              <Input
                value={row.key}
                onChange={(e) => setRow(idx, "key", e.target.value)}
                placeholder={leftPlaceholder}
                spellCheck={false}
              />
            </FormControl>

            <FormControl>
              <FormLabel mb={1} fontSize="xs" color="gray.500">
                {rightLabel}
              </FormLabel>
              {rightType === "number" ? (
                <NumberInput
                  value={row.value === "" ? "" : Number(row.value)}
                  onChange={(_, n) => setRow(idx, "value", String(isNaN(n) ? "" : n))}
                >
                  <NumberInputField placeholder={rightPlaceholder} />
                </NumberInput>
              ) : (
                <Input
                  value={row.value}
                  onChange={(e) => setRow(idx, "value", e.target.value)}
                  placeholder={rightPlaceholder}
                  spellCheck={false}
                />
              )}
            </FormControl>

            <Tooltip label="Remove row">
              <IconButton
                aria-label="Remove row"
                icon={<DeleteIcon />}
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={() => removeRow(idx)}
              />
            </Tooltip>
          </HStack>
        ))}
      </VStack>

      <Button onClick={addRow} leftIcon={<AddIcon />} size="sm" alignSelf="flex-start" variant="ghost">
        Add Row
      </Button>

      {helper && (
        <Text fontSize="sm" color="gray.500">
          {helper}
        </Text>
      )}
    </VStack>
  );
};

/* ---------- Chips (comma separated) ---------- */

const ChipsInput = ({ label, value = [], onChange, placeholder, helper, extraControls }) => {
  const [text, setText] = useState((value || []).join(", "));

  useEffect(() => {
    setText((value || []).join(", "));
  }, [value]);

  const commit = (t) => {
    const arr = (t || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    onChange(arr);
  };

  return (
    <FormControl>
      <HStack justify="space-between" align="center" mb={1}>
        <FormLabel mb={0}>{label}</FormLabel>
        {extraControls}
      </HStack>
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => commit(text)}
        placeholder={placeholder}
      />
      {helper && <FormHelperText>{helper}</FormHelperText>}
      <HStack mt={2} wrap="wrap">
        {(value || []).map((t, i) => (
          <Badge key={`${t}-${i}`} colorScheme="purple" variant="subtle">
            {t}
          </Badge>
        ))}
      </HStack>
    </FormControl>
  );
};

/* ---------- Main Modal ---------- */

const ProductEditorModal = ({
  isOpen,
  onClose,
  form,
  setForm,
  isEditing,
  isSaving,
  onSave,
  onRequestDelete,
}) => {
  const {
    isOpen: isHelpOpen,
    onOpen: onHelpOpen,
    onClose: onHelpClose,
  } = useDisclosure();

  /* basic fields (local) */
  const [localForm, setLocalForm] = useState(form);
  useEffect(() => {
    setLocalForm(form);
  }, [form, isOpen]);

  /* Sizes + Part Numbers toggle */
  const [spMode, setSpMode] = useState("form"); // 'form' | 'json'
  const buildSizePartPairs = (f) => {
    const pn = normalizePartNumbers(f.partNumbers);
    const union = unique([...(f.sizes || []), ...Object.keys(pn)]);
    return union.map((sz) => ({ key: sz, value: pn[sz] || "" }));
  };
  const [sizePartPairs, setSizePartPairs] = useState(buildSizePartPairs(form));
  const buildSpJson = (f) =>
    JSON.stringify(
      { sizes: f.sizes || [], partNumbers: normalizePartNumbers(f.partNumbers) },
      null,
      2
    );
  const [spJsonRaw, setSpJsonRaw] = useState(buildSpJson(form));
  const spJsonStatus = useMemo(() => validateJson(spJsonRaw), [spJsonRaw]);

  useEffect(() => {
    setSizePartPairs(buildSizePartPairs(localForm));
    setSpJsonRaw(buildSpJson(localForm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localForm.id]);

  /* Historical prices toggle */
  const [hpMode, setHpMode] = useState("form"); // 'form' | 'json'
  const [histPairs, setHistPairs] = useState(mapToPairs(form.historicalPrices));
  const [hpJsonRaw, setHpJsonRaw] = useState(JSON.stringify(form.historicalPrices || {}, null, 2));
  const hpJsonStatus = useMemo(() => validateJson(hpJsonRaw), [hpJsonRaw]);

  useEffect(() => {
    setHistPairs(mapToPairs(localForm.historicalPrices));
    setHpJsonRaw(JSON.stringify(localForm.historicalPrices || {}, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localForm.id]);

  /* ===== Controls for admin_upsertProduct ===== */
  // _replace flags (lists/maps)
  const [replacePartNumbers, setReplacePartNumbers] = useState(false);
  const [replaceSizes, setReplaceSizes] = useState(false);
  const [replaceCoverage, setReplaceCoverage] = useState(false);
  const [replaceTags, setReplaceTags] = useState(false);
  const [replaceHistoricalPrices, setReplaceHistoricalPrices] = useState(false);

  // Per-field "delete ALL" toggles for lists/maps
  const [deleteSizesParts, setDeleteSizesParts] = useState(false);
  const [deleteCoverage, setDeleteCoverage] = useState(false);
  const [deleteTags, setDeleteTags] = useState(false);
  const [deleteHistoricalPrices, setDeleteHistoricalPrices] = useState(false);

  // Nuclear delete for scalar-ish fields (advanced)
  const deletableFields = [
    "description",
    "info",
    "imageUrl",
    "ifuUrl",
    "category",
    "ptype",
    "mue",
  ];
  const [toDelete, setToDelete] = useState([]);

  // _unsetHistoricalDates
  const existingHistoDates = useMemo(
    () => Object.keys(localForm?.historicalPrices || {}).sort(),
    [localForm?.historicalPrices]
  );
  const [unsetDates, setUnsetDates] = useState([]);
  const [manualUnsetDate, setManualUnsetDate] = useState("");

  const toggleUnsetDate = (d) =>
    setUnsetDates((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  // noAutoStamp + priceDate
  const [noAutoStamp, setNoAutoStamp] = useState(false);
  const [priceDate, setPriceDate] = useState("");

  /* derived: canSave */
  const nameOk = (localForm?.name || "").trim().length > 0;
  const jsonOk =
    (spMode === "json" ? spJsonStatus.ok : true) &&
    (hpMode === "json" ? hpJsonStatus.ok : true);
  const canSave = nameOk && jsonOk;

  const switchSpMode = (toJson) => {
    if (toJson) {
      const sizesArr = pairsToArr(sizePartPairs);
      const pnMap = pairsToMap(sizePartPairs);
      setSpJsonRaw(JSON.stringify({ sizes: sizesArr, partNumbers: pnMap }, null, 2));
      setSpMode("json");
    } else {
      if (spJsonStatus.ok) {
        const { sizes = [], partNumbers = {} } = spJsonStatus.value || {};
        const pnNorm = normalizePartNumbers(partNumbers);
        const union = unique([...(sizes || []), ...Object.keys(pnNorm)]);
        setSizePartPairs(union.map((sz) => ({ key: sz, value: pnNorm[sz] || "" })));
      }
      setSpMode("form");
    }
  };

  const switchHpMode = (toJson) => {
    if (toJson) {
      const hpMap = pairsToMap(histPairs, { numericValues: true });
      setHpJsonRaw(JSON.stringify(hpMap, null, 2));
      setHpMode("json");
    } else {
      if (hpJsonStatus.ok) {
        setHistPairs(mapToPairs(hpJsonStatus.value || {}));
      }
      setHpMode("form");
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3} align="center">
              <Text>{isEditing ? `Edit ${form.name || ""}` : "Add Product"}</Text>
              <Tooltip label="Open a quick guide explaining merge, replace, and delete behavior.">
                <Button size="xs" variant="outline" onClick={onHelpOpen}>
                  How does this work?
                </Button>
              </Tooltip>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              {/* ID / Name */}
              <HStack spacing={4} align="start">
                <FormControl>
                  <FormLabel>Product ID</FormLabel>
                  <Input
                    value={localForm.id || ""}
                    onChange={(e) => setLocalForm((s) => ({ ...s, id: e.target.value }))}
                    placeholder="e.g., vitograft"
                    spellCheck={false}
                  />
                  <FormHelperText>Leave blank to auto-generate from the name.</FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={localForm.name || ""}
                    onChange={(e) => setLocalForm((s) => ({ ...s, name: e.target.value }))}
                    placeholder="VitoGraft"
                  />
                </FormControl>
              </HStack>

              {/* qCode / ptype / mue */}
              <HStack spacing={4} align="start">
                <FormControl>
                  <FormLabel>Q Code</FormLabel>
                  <Input
                    value={localForm.qCode || ""}
                    onChange={(e) => setLocalForm((s) => ({ ...s, qCode: e.target.value }))}
                    placeholder="Q4317"
                    spellCheck={false}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Type</FormLabel>
                  <Select
                    value={localForm.ptype || ""}
                    onChange={(e) => setLocalForm((s) => ({ ...s, ptype: e.target.value }))}
                  >
                    <option value="">—</option>
                    <option value="restricted">restricted</option>
                    <option value="public">public</option>
                    <option value="internal">internal</option>
                  </Select>
                  <FormHelperText>Optional internal label.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>MUE</FormLabel>
                  <NumberInput min={0} value={Number(localForm.mue ?? 0)}>
                    <NumberInputField
                      onChange={(e) =>
                        setLocalForm((s) => ({ ...s, mue: Number(e.target.value || 0) }))
                      }
                      placeholder="0"
                    />
                  </NumberInput>
                </FormControl>
              </HStack>

              {/* Category / Price */}
              <HStack spacing={4} align="start">
                <FormControl>
                  <FormLabel>Category</FormLabel>
                  <Input
                    value={localForm.category || ""}
                    onChange={(e) => setLocalForm((s) => ({ ...s, category: e.target.value }))}
                    placeholder="Full ASP Product"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Price</FormLabel>
                  <NumberInput
                    min={0}
                    precision={2}
                    value={localForm.price ?? ""}
                    onChange={(_, valueNumber) =>
                      setLocalForm((s) => ({ ...s, price: isNaN(valueNumber) ? "" : valueNumber }))
                    }
                  >
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                  <FormHelperText>Current price per unit.</FormHelperText>
                </FormControl>
              </HStack>

              {/* Coverage / Tags */}
              <HStack spacing={4} align="start" alignItems="flex-start">
                <ChipsInput
                  label={
                    <HStack>
                      <Text>Coverage</Text>
                      <Tooltip label="E.g., Medicare Part B, Commercial. Used for filtering and UI hints.">
                        <span>
                          <IconButton
                            aria-label="Coverage help"
                            icon={<InfoIcon />}
                            size="xs"
                            variant="ghost"
                          />
                        </span>
                      </Tooltip>
                    </HStack>
                  }
                  value={localForm.coverage || []}
                  onChange={(arr) => setLocalForm((s) => ({ ...s, coverage: arr }))}
                  placeholder="Medicare Part B, Commercial …"
                  helper="Comma-separated."
                  extraControls={
                    <HStack spacing={2}>
                      <Tooltip label="If you removed coverage items and expect them gone, check this. The list will be overwritten with what you see." hasArrow>
                        <span>
                          <Checkbox
                            size="sm"
                            isChecked={replaceCoverage}
                            onChange={(e) => setReplaceCoverage(e.target.checked)}
                          >
                            Overwrite when removing
                          </Checkbox>
                        </span>
                      </Tooltip>
                      <Tooltip label="Danger: clears ALL coverage values for this product." hasArrow>
                        <span>
                          <Checkbox
                            size="sm"
                            colorScheme="red"
                            isChecked={deleteCoverage}
                            onChange={(e) => setDeleteCoverage(e.target.checked)}
                          >
                            Delete ALL coverage
                          </Checkbox>
                        </span>
                      </Tooltip>
                    </HStack>
                  }
                />
                <ChipsInput
                  label={
                    <HStack>
                      <Text>Tags</Text>
                      <Tooltip label="Free-form labels for search and grouping (e.g., wound, asp).">
                        <span>
                          <IconButton
                            aria-label="Tags help"
                            icon={<InfoIcon />}
                            size="xs"
                            variant="ghost"
                          />
                        </span>
                      </Tooltip>
                    </HStack>
                  }
                  value={localForm.tags || []}
                  onChange={(arr) => setLocalForm((s) => ({ ...s, tags: arr }))}
                  placeholder="asp, wound, sterile"
                  helper="Comma-separated."
                  extraControls={
                    <HStack spacing={2}>
                      <Tooltip label="If you removed tags and expect them gone, check this. The list will be overwritten with what you see." hasArrow>
                        <span>
                          <Checkbox
                            size="sm"
                            isChecked={replaceTags}
                            onChange={(e) => setReplaceTags(e.target.checked)}
                          >
                            Overwrite when removing
                          </Checkbox>
                        </span>
                      </Tooltip>
                      <Tooltip label="Danger: clears ALL tags for this product." hasArrow>
                        <span>
                          <Checkbox
                            size="sm"
                            colorScheme="red"
                            isChecked={deleteTags}
                            onChange={(e) => setDeleteTags(e.target.checked)}
                          >
                            Delete ALL tags
                          </Checkbox>
                        </span>
                      </Tooltip>
                    </HStack>
                  }
                />
              </HStack>

              {/* Description */}
              <FormControl>
                <HStack justify="space-between" align="center">
                  <FormLabel>Description</FormLabel>
                  <Tooltip label="Plain-language description shown to users.">
                    <span>
                      <IconButton
                        aria-label="Description help"
                        icon={<InfoIcon />}
                        size="xs"
                        variant="ghost"
                      />
                    </span>
                  </Tooltip>
                </HStack>
                <Textarea
                  value={localForm.description || ""}
                  onChange={(e) => setLocalForm((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Product description"
                />
              </FormControl>

              {/* Info */}
              <FormControl>
                <HStack justify="space-between" align="center">
                  <FormLabel>Info</FormLabel>
                  <Tooltip label="Extra notes: coding, LCD hints, brief internal usage tips.">
                    <span>
                      <IconButton
                        aria-label="Info help"
                        icon={<InfoIcon />}
                        size="xs"
                        variant="ghost"
                      />
                    </span>
                  </Tooltip>
                </HStack>
                <Textarea
                  value={localForm.info || ""}
                  onChange={(e) => setLocalForm((s) => ({ ...s, info: e.target.value }))}
                  placeholder="HCPCS Level II …"
                />
              </FormControl>

              {/* IFU / Image */}
              <HStack spacing={4} align="end">
                <FormControl>
                  <HStack justify="space-between" align="center">
                    <FormLabel>IFU File</FormLabel>
                    <Tooltip label="File name or path to the official Instructions For Use PDF.">
                      <span>
                        <IconButton
                          aria-label="IFU help"
                          icon={<InfoIcon />}
                          size="xs"
                          variant="ghost"
                        />
                      </span>
                    </Tooltip>
                  </HStack>
                  <Input
                    value={localForm.ifuUrl || ""}
                    onChange={(e) => setLocalForm((s) => ({ ...s, ifuUrl: e.target.value }))}
                    placeholder="IFU-vitograft.pdf"
                  />
                  <FormHelperText>Served from your assets bucket/path.</FormHelperText>
                </FormControl>
                <FormControl>
                  <HStack justify="space-between" align="center">
                    <FormLabel>Image File</FormLabel>
                    <Tooltip label="File name for the image shown on the site.">
                      <span>
                        <IconButton
                          aria-label="Image help"
                          icon={<InfoIcon />}
                          size="xs"
                          variant="ghost"
                        />
                      </span>
                    </Tooltip>
                  </HStack>
                  <Input
                    value={localForm.imageUrl || ""}
                    onChange={(e) => setLocalForm((s) => ({ ...s, imageUrl: e.target.value }))}
                    placeholder="vitograft.webp"
                  />
                  <FormHelperText>Served from /assets/img/graft/ or similar.</FormHelperText>
                </FormControl>
              </HStack>

              {/* Visible */}
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Visible in product lists</FormLabel>
                <Switch
                  isChecked={!!localForm.isVisible}
                  onChange={(e) => setLocalForm((s) => ({ ...s, isVisible: e.target.checked }))}
                />
              </FormControl>

              <Divider />

              {/* Sizes & Part Numbers */}
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between" align="center">
                  <HStack>
                    <FormLabel m={0}>Sizes &amp; Part Numbers</FormLabel>
                    <Tooltip label="Match sizes (e.g. 2x3) to manufacturer part numbers.">
                      <span>
                        <IconButton
                          aria-label="Sizes & part numbers help"
                          icon={<InfoIcon />}
                          size="xs"
                          variant="ghost"
                        />
                      </span>
                    </Tooltip>
                  </HStack>
                  <HStack spacing={3}>
                    <HStack>
                      <Badge variant="subtle" colorScheme={spMode === "form" ? "teal" : "gray"}>
                        Form
                      </Badge>
                      <Switch
                        isChecked={spMode === "json"}
                        onChange={(e) => switchSpMode(e.target.checked)}
                      />
                      <Badge variant="subtle" colorScheme={spMode === "json" ? "purple" : "gray"}>
                        JSON
                      </Badge>
                    </HStack>
                  </HStack>
                </HStack>

                <HStack spacing={4} align="center">
                  <Tooltip label="If you removed or changed rows and expect them to replace what is stored, check this." hasArrow>
                    <span>
                      <Checkbox
                        size="sm"
                        isChecked={replaceSizes && replacePartNumbers}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setReplaceSizes(v);
                          setReplacePartNumbers(v);
                        }}
                      >
                        Overwrite when removing
                      </Checkbox>
                    </span>
                  </Tooltip>
                  <Tooltip label="Danger: clears ALL sizes and ALL part numbers for this product." hasArrow>
                    <span>
                      <Checkbox
                        size="sm"
                        colorScheme="red"
                        isChecked={deleteSizesParts}
                        onChange={(e) => setDeleteSizesParts(e.target.checked)}
                      >
                        Delete ALL sizes &amp; part numbers
                      </Checkbox>
                    </span>
                  </Tooltip>
                </HStack>

                {spMode === "form" ? (
                  <PairListEditor
                    leftLabel="AxB Size"
                    rightLabel="Part #"
                    pairs={sizePartPairs}
                    setPairs={setSizePartPairs}
                    leftPlaceholder="e.g., 2x3"
                    rightPlaceholder="e.g., VG-023"
                    helper="Each row adds a size and its part number."
                  />
                ) : (
                  <FormControl isInvalid={!spJsonStatus.ok}>
                    <Textarea
                      value={spJsonRaw}
                      onChange={(e) => setSpJsonRaw(e.target.value)}
                      fontFamily="mono"
                      rows={10}
                    />
                    {!spJsonStatus.ok ? (
                      <FormErrorMessage>{spJsonStatus.error}</FormErrorMessage>
                    ) : (
                      <FormHelperText>
                        {"{ sizes: string[], partNumbers: { [size]: partNumber } }"}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              </VStack>

              <Divider />

              {/* Historical Prices */}
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between" align="center">
                  <HStack>
                    <FormLabel m={0}>Historical Pricing</FormLabel>
                    <Tooltip label="Optional history of prices by date (for reporting only).">
                      <span>
                        <IconButton
                          aria-label="Historical pricing help"
                          icon={<InfoIcon />}
                          size="xs"
                          variant="ghost"
                        />
                      </span>
                    </Tooltip>
                  </HStack>
                  <HStack spacing={3}>
                    <HStack>
                      <Badge variant="subtle" colorScheme={hpMode === "form" ? "teal" : "gray"}>
                        Form
                      </Badge>
                      <Switch
                        isChecked={hpMode === "json"}
                        onChange={(e) => switchHpMode(e.target.checked)}
                      />
                      <Badge variant="subtle" colorScheme={hpMode === "json" ? "purple" : "gray"}>
                        JSON
                      </Badge>
                    </HStack>
                  </HStack>
                </HStack>

                <HStack spacing={4} align="center">
                  <Tooltip label="Use only the entries you see here; replace any existing history." hasArrow>
                    <span>
                      <Checkbox
                        size="sm"
                        isChecked={replaceHistoricalPrices}
                        onChange={(e) => setReplaceHistoricalPrices(e.target.checked)}
                      >
                        Overwrite history
                      </Checkbox>
                    </span>
                  </Tooltip>
                  <Tooltip label="Danger: clears ALL past price entries for this product." hasArrow>
                    <span>
                      <Checkbox
                        size="sm"
                        colorScheme="red"
                        isChecked={deleteHistoricalPrices}
                        onChange={(e) => setDeleteHistoricalPrices(e.target.checked)}
                      >
                        Delete ALL price history
                      </Checkbox>
                    </span>
                  </Tooltip>
                </HStack>

                {hpMode === "form" ? (
                  <PairListEditor
                    leftLabel="Date (YYYY-MM-DD)"
                    rightLabel="Price"
                    pairs={histPairs}
                    setPairs={setHistPairs}
                    leftPlaceholder="2025-10-01"
                    rightPlaceholder="4770.00"
                    rightType="number"
                    helper="Each row records a date and price."
                  />
                ) : (
                  <FormControl isInvalid={!hpJsonStatus.ok}>
                    <Textarea
                      value={hpJsonRaw}
                      onChange={(e) => setHpJsonRaw(e.target.value)}
                      fontFamily="mono"
                      rows={8}
                    />
                    {!hpJsonStatus.ok ? (
                      <FormErrorMessage>{hpJsonStatus.error}</FormErrorMessage>
                    ) : (
                      <FormHelperText>{"{ '2025-10-01': 4770.00, ... }"}</FormHelperText>
                    )}
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel mb={1}>Remove specific history dates</FormLabel>
                  <HStack wrap="wrap" spacing={2}>
                    {existingHistoDates.length === 0 && (
                      <Badge colorScheme="gray" variant="subtle">
                        No existing dates
                      </Badge>
                    )}
                    {existingHistoDates.map((d) => {
                      const active = unsetDates.includes(d);
                      return (
                        <Tag
                          key={d}
                          size="md"
                          variant={active ? "solid" : "subtle"}
                          colorScheme={active ? "red" : "gray"}
                          cursor="pointer"
                          onClick={() => toggleUnsetDate(d)}
                        >
                          <TagLabel>{d}</TagLabel>
                          {active && <TagCloseButton onClick={() => toggleUnsetDate(d)} />}
                        </Tag>
                      );
                    })}
                  </HStack>
                  <HStack mt={2}>
                    <Input
                      type="date"
                      value={manualUnsetDate}
                      onChange={(e) => setManualUnsetDate(e.target.value)}
                      maxW="220px"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        const d = (manualUnsetDate || "").trim();
                        if (d && !unsetDates.includes(d)) setUnsetDates((prev) => [...prev, d]);
                        setManualUnsetDate("");
                      }}
                    >
                      Add Date
                    </Button>
                  </HStack>
                  <FormHelperText>Soft delete individual entries without wiping everything.</FormHelperText>
                </FormControl>

                <SimpleGrid columns={[1, 2]} spacing={4}>
                  <FormControl display="flex" alignItems="center">
                    <Tooltip label="Normally, changing the price auto-adds an entry with today’s date. Check this to disable that." hasArrow>
                      <span>
                        <Checkbox
                          isChecked={noAutoStamp}
                          onChange={(e) => setNoAutoStamp(e.target.checked)}
                        >
                          Disable auto history for price changes
                        </Checkbox>
                      </span>
                    </Tooltip>
                  </FormControl>
                  <FormControl>
                    <FormLabel mb={1}>Custom Price Date (optional)</FormLabel>
                    <Input
                      type="date"
                      value={priceDate}
                      onChange={(e) => setPriceDate(e.target.value)}
                      placeholder="YYYY-MM-DD"
                    />
                    <FormHelperText>
                      If set, auto history uses this date instead of today.
                    </FormHelperText>
                  </FormControl>
                </SimpleGrid>
              </VStack>

              {/* Advanced nuclear deletes for scalar fields */}
              <Divider />
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between" align="center">
                  <Text fontWeight="semibold">Advanced: Delete ENTIRE Fields (ALL data)</Text>
                  <Tooltip label="These options remove whole fields from the product record. Only use if something is truly wrong.">
                    <span>
                      <IconButton
                        aria-label="Advanced delete help"
                        icon={<InfoIcon />}
                        size="xs"
                        variant="ghost"
                      />
                    </span>
                  </Tooltip>
                </HStack>
                <Text fontSize="sm" color="red.500">
                  Use sparingly. These clear entire fields. You&apos;d need to retype values to restore them.
                </Text>
                <FormControl>
                  <HStack wrap="wrap" spacing={2}>
                    {deletableFields.map((f) => {
                      const checked = toDelete.includes(f);
                      return (
                        <Tooltip
                          key={f}
                          label={`Delete ALL data for '${f}' on this product.`}
                          hasArrow
                        >
                          <span>
                            <Checkbox
                              isChecked={checked}
                              onChange={(e) =>
                                setToDelete((prev) =>
                                  e.target.checked ? [...prev, f] : prev.filter((x) => x !== f)
                                )
                              }
                            >
                              Delete ALL <code>{f}</code>
                            </Checkbox>
                          </span>
                        </Tooltip>
                      );
                    })}
                  </HStack>
                </FormControl>
              </VStack>
            </VStack>
          </ModalBody>

          <ModalFooter>
            {isEditing && (
              <Tooltip label="Permanently delete this product record.">
                <Button
                  leftIcon={<DeleteIcon />}
                  colorScheme="red"
                  variant="outline"
                  mr="auto"
                  onClick={onRequestDelete}
                >
                  Delete Product
                </Button>
              </Tooltip>
            )}

            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>

            {/* Build final payload locally and hand it up to parent */}
            <Button
              colorScheme="teal"
              isDisabled={!canSave || isSaving}
              isLoading={isSaving}
              onClick={() => {
                let finalProduct = { ...localForm };

                // Sizes & Part Numbers
                if (spMode === "form") {
                  const sizesArr = pairsToArr(sizePartPairs);
                  const pnMap = pairsToMap(sizePartPairs);
                  finalProduct.sizes = sizesArr;
                  finalProduct.partNumbers = pnMap;
                } else if (spMode === "json" && spJsonStatus.ok) {
                  const { sizes = [], partNumbers = {} } = spJsonStatus.value || {};
                  finalProduct.sizes = Array.isArray(sizes) ? sizes : [];
                  finalProduct.partNumbers = Object.fromEntries(
                    Object.entries(partNumbers).map(([k, v]) => [String(k), String(v ?? "")])
                  );
                }

                // Historical Prices
                if (hpMode === "form") {
                  finalProduct.historicalPrices = pairsToMap(histPairs, { numericValues: true });
                } else if (hpMode === "json" && hpJsonStatus.ok) {
                  const raw = hpJsonStatus.value || {};
                  finalProduct.historicalPrices = Object.fromEntries(
                    Object.entries(raw).map(([k, v]) => [k, Number(v)])
                  );
                }

                // Coerce numerics
                if (finalProduct.price !== undefined && finalProduct.price !== "") {
                  finalProduct.price = Number(finalProduct.price);
                }
                if (finalProduct.mue !== undefined && finalProduct.mue !== "") {
                  finalProduct.mue = Number(finalProduct.mue);
                }

                // Arrays for coverage/tags
                if (!Array.isArray(finalProduct.coverage)) finalProduct.coverage = [];
                if (!Array.isArray(finalProduct.tags)) finalProduct.tags = [];

                // Ensure id
                const hasId = (finalProduct.id || "").trim().length > 0;
                if (!hasId && (finalProduct.name || "").trim()) {
                  finalProduct.id = slug(finalProduct.name);
                }

                // ==== Inject control flags only if needed ====
                const _replace = {};
                if (replacePartNumbers) _replace.partNumbers = true;
                if (replaceSizes) _replace.sizes = true;
                if (replaceCoverage) _replace.coverage = true;
                if (replaceTags) _replace.tags = true;
                if (replaceHistoricalPrices) _replace.historicalPrices = true;
                if (Object.keys(_replace).length) finalProduct._replace = _replace;

                // Build delete list from advanced + per-section nuclear toggles
                const deleteFields = new Set(toDelete);

                if (deleteSizesParts) {
                  deleteFields.add("sizes");
                  deleteFields.add("partNumbers");
                }
                if (deleteCoverage) deleteFields.add("coverage");
                if (deleteTags) deleteFields.add("tags");
                if (deleteHistoricalPrices) deleteFields.add("historicalPrices");

                if (deleteFields.size) {
                  finalProduct._delete = Array.from(deleteFields);
                }

                const cleanedUnset = unique(unsetDates).filter(Boolean);
                if (cleanedUnset.length) finalProduct._unsetHistoricalDates = cleanedUnset;

                if (noAutoStamp) finalProduct.noAutoStamp = true;
                if ((priceDate || "").trim()) finalProduct.priceDate = priceDate.trim();

                // 🔥 Make delete semantics intuitive:
                // If a field is marked for _delete, drop the actual value
                // and any _replace flag for that field so the backend
                // truly removes it.
                if (Array.isArray(finalProduct._delete) && finalProduct._delete.length) {
                  for (const field of finalProduct._delete) {
                    delete finalProduct[field];
                    if (finalProduct._replace && field in finalProduct._replace) {
                      delete finalProduct._replace[field];
                    }
                  }
                  if (finalProduct._replace && Object.keys(finalProduct._replace).length === 0) {
                    delete finalProduct._replace;
                  }
                }

                onSave(finalProduct);
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Tutorial Drawer */}
      <Drawer isOpen={isHelpOpen} placement="right" onClose={onHelpClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>How product updates work</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4} pb={6}>
              <Text fontSize="sm">
                Here&apos;s how edits behave so you don&apos;t have to worry about breaking anything.
              </Text>

              <Text fontWeight="semibold" fontSize="sm">
                1. Normal fields (name, description, info, etc.)
              </Text>
              <Text fontSize="sm">
                Just change the values and hit <strong>Save</strong>. The new values replace what was there
                before.
              </Text>

              <Text fontWeight="semibold" fontSize="sm">
                2. Lists like Coverage, Tags, Sizes
              </Text>
              <Text fontSize="sm">
                By default, these lists <strong>merge</strong>:
              </Text>
              <Text as="ul" fontSize="sm" pl={4}>
                <li>New items you add are added on top of what&apos;s already stored.</li>
                <li>Items you remove in the UI do not disappear automatically.</li>
              </Text>
              <Text fontSize="sm">
                If you remove items and expect them to be gone, check{" "}
                <strong>“Overwrite when removing”</strong> for that section. Then the list in the
                database becomes exactly what you see on screen.
              </Text>

              <Text fontWeight="semibold" fontSize="sm">
                3. Price history
              </Text>
              <Text fontSize="sm">
                You can optionally keep a history of prices by date:
              </Text>
              <Text as="ul" fontSize="sm" pl={4}>
                <li>Rows you add are merged in.</li>
                <li>Use the chips under &quot;Remove specific history dates&quot; to drop a few dates.</li>
                <li>
                  Check <strong>“Overwrite history”</strong> if the history should become exactly what
                  you entered.
                </li>
              </Text>

              <Text fontWeight="semibold" fontSize="sm">
                4. Auto history for price changes
              </Text>
              <Text fontSize="sm">
                When the price changes, the system can automatically add a new history entry using
                today&apos;s date (or a custom date you choose).
              </Text>
              <Text fontSize="sm">
                If you don&apos;t want that, check{" "}
                <strong>“Disable auto history for price changes”</strong>.
              </Text>

              <Text fontWeight="semibold" fontSize="sm" color="red.500">
                5. “Delete ALL …” checkboxes
              </Text>
              <Text fontSize="sm">
                Anything labeled <strong>“Delete ALL …”</strong> is a nuclear option:
              </Text>
              <Text as="ul" fontSize="sm" pl={4}>
                <li>It clears all values for that field (e.g., all coverage, all tags, all history).</li>
                <li>The next save removes that field from the product record.</li>
              </Text>
              <Text fontSize="sm">
                If you&apos;re ever unsure, leave delete options off and just use normal editing or
                &quot;Overwrite&quot; for that section.
              </Text>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ProductEditorModal;
